export * from './affiliate-links';
export * from './analytics';
export * from './askStanley';
export * from './audience';
export * from './customers';
export * from './email-flows/types';
export * from './income';
export * from './user';
export * from './referrals';
export * from './courses';

import axios, { type AxiosInstance } from 'axios';
import { useAuthStore, useGlobalStore } from '@/stores';
import { configSettings } from '../config';
import { browserTracingIntegration, init } from '@sentry/react';

export const mainApi = axios.create({
    baseURL: configSettings.apiUrl,
});
export const authApi = axios.create({
    baseURL: configSettings.authApiUrl,
});
export const chatAiApi = axios.create({
    baseURL: configSettings.chatAIApiUrlV2,
});

// Response interceptor
function attachInterceptorLogic(api: AxiosInstance) {
    // add headers to all requests
    api.interceptors.request.use(request => {
        const token = useAuthStore.getState().token;
        const UUID = useGlobalStore.getState().UUID;
        const sessionUUID = useGlobalStore.getState().sessionUUID;
        const getDeviceToken = useGlobalStore.getState().getDeviceToken;
        if (token) {
            request.headers['Authorization'] = `Bearer ${token}`;
        }
        request.headers['x-request-id'] = UUID;
        request.headers['x-session-uuid'] = sessionUUID;
        request.headers['x-session-id'] = getDeviceToken();
        return request;
    });

    api.interceptors.response.use(
        response => {
            const isLoginRoute = response.config?.url?.endsWith('/login');
            const isMFAVerifyRoute = response.config?.url?.endsWith('/re-verify');
            const isRecoveryRoute = response.config?.url?.endsWith('/recovery');
            const ipAddress = response?.data?.ip;
            if (typeof response.data === 'object' && ipAddress) {
                useGlobalStore.setState({ ipAddress });
            }

            // tag devices that hit our auth endpoints
            const deviceToken = response.data?.deviceToken;
            if ((isLoginRoute || isMFAVerifyRoute || isRecoveryRoute) && deviceToken) {
                useGlobalStore.setState({ deviceToken });
            }
            return response;
        },
        error => {
            const status = error?.response?.status; // Possibility of response being undefined.
            const isAnyLoginRoute = error.response?.config?.url?.endsWith('/login');
            const isMFAVerifyRoute = error.response?.config?.url?.endsWith('/re-verify');
            const isRecoveryRoute = error.response?.config?.url?.endsWith('/recovery');
            // TODO: Gtag
            // $gtag.event('exception', { description: error, fatal: false });

            // We do not force logout users due to a failed login or logout attempt
            const isLoginLogoutRoute =
                error.config?.url?.endsWith('v1/login') || error.config?.url?.endsWith('v1/logout');

            // Even on error, we want to tag the device
            const deviceToken = error.response?.data?.deviceToken;
            if ((isAnyLoginRoute || isMFAVerifyRoute || isRecoveryRoute) && deviceToken) {
                useGlobalStore.setState({ deviceToken });
            }

            // Match Vue behavior: only logout on 401 if user is authenticated (has token)
            // This prevents race conditions where API calls fail before user data is cached
            if (!isLoginLogoutRoute && status === 401 && useAuthStore.getState().isAuthenticated) {
                useAuthStore.getState().logout();
            }

            return Promise.reject(error);
        }
    );
}

attachInterceptorLogic(mainApi);
attachInterceptorLogic(authApi);
attachInterceptorLogic(chatAiApi);

if (['staging', 'production'].includes(configSettings.environment)) {
    init({
        dsn: configSettings.sentryDsn,
        environment: configSettings.environment,
        // release: configSettings.releaseVersion, // TODO
        tracesSampleRate: 0, // or adjust sampling if you want performance tracing
        integrations: [browserTracingIntegration()],
        beforeSend(event, hint) {
            let ignoreEvent = false;

            const exValues = event.exception?.values || [];

            for (const ex of exValues) {
                const value = ex.value || '';
                const type = ex.type || '';

                if (value.includes('ResizeObserver')) ignoreEvent = true;
                else if (type === 'NavigationDuplicated') ignoreEvent = true;
                else if (type === 'Event') {
                    console.warn('Ignoring Event', type, value);
                    ignoreEvent = true;
                } else if (type === 'TypeError') {
                    if (
                        value.includes('naturalWidth') ||
                        value === "Cannot read properties of null (reading 'classList')" ||
                        value.includes("null is not an object (evaluating 't.target.parentNode.classList'")
                    ) {
                        console.warn('Ignoring Error', type, value);
                        ignoreEvent = true;
                    }
                } else if (type === 'SyntaxError' && value.includes('Unexpected token')) {
                    ignoreEvent = true;
                } else if (type === 'Error') {
                    const is4xx = /\s4[0-9]{2}$/.test(value);
                    const isRequestFailure = value.toLowerCase().includes('request failed with status code');

                    if (
                        value === 'Network Error' ||
                        (isRequestFailure && is4xx) ||
                        value === 'undefined' ||
                        value.includes('Navigation cancelled from') ||
                        value.includes('Redirected when going from')
                    ) {
                        console.warn('Ignoring Error', type, value);
                        ignoreEvent = true;
                    }
                } else if (hint?.originalException === 'Timeout') {
                    ignoreEvent = true;
                }

                if (ignoreEvent) break;
            }

            return ignoreEvent ? null : event;
        },
    });
}
